import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'

import { useTranslation } from 'app/i18n/client'

import CalendarInIcon from 'public/icons/calendar-in.svg'
import CalendarOutIcon from 'public/icons/calendar-out.svg'
import CloseIcon from 'public/icons/close.svg'

import { Modal } from 'app/components/atoms'
import GuidelinePopup from '../GuidelinePopup'
import { getJapanTimeZoneToday } from 'utils/toJapanTimeZone'

import { setDateStyles } from '../utils'
import Loading from 'app/components/atoms/Loading'
import DateRangeSelect from '../DateRangeSelect'

interface PopupPickerProps {
  selectedValue: (Date | null)[]
  label?: string
  containerClassNames?: string
  isError?: boolean
  buttonContainerClassNames?: string
  buttonClassNames?: string
  checkIn: string
  checkOut: string
  currentActiveStartDate: Date
  locale?: string
  headerTitle: string
  disabledDates?: Date[]
  ebisuDisabledDates?: Date[]
  isLoading?: boolean
  disabled?: boolean
  onSelectValue?: (value: (Date | null)[]) => void
  setCurrentActiveStartDate: (value: Date) => void
  onClick?: () => void
  onOpenMaxNightModal: () => void
  onCalendarOpen?: () => void
}

const PopupPicker: React.FunctionComponent<PopupPickerProps> = ({
  label,
  containerClassNames,
  buttonClassNames,
  buttonContainerClassNames,
  isError,
  checkIn,
  checkOut,
  currentActiveStartDate,
  selectedValue,
  headerTitle,
  disabledDates,
  ebisuDisabledDates,
  isLoading,
  disabled,
  onSelectValue,
  setCurrentActiveStartDate,
  onClick,
  onOpenMaxNightModal,
  onCalendarOpen,
}) => {
  const today = getJapanTimeZoneToday().toDate()
  const calendarRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)

  const handleCancel = () => onSelectValue && onSelectValue([null, null])

  const openCalendar = () => {
    if (disabled) return
    onCalendarOpen && onCalendarOpen()
    setOpen(true)
  }

  const closeCalendar = () => {
    const [startDate, endDate] = selectedValue
    if (startDate !== null && endDate === null) handleCancel()
    setOpen(false)
  }

  useEffect(() => {
    if (open) setTimeout(() => setDateStyles(calendarRef, today), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, calendarRef?.current])

  return (
    <div
      className={classNames('w-full', disabled && 'opacity-70')}
      onClick={onClick}
    >
      <button
        type="button"
        disabled={disabled}
        className="w-full"
        onClick={openCalendar}
      >
        {label && (
          <label className="mb-2.5 flex flex-start text-xs text-cream uppercase text-left">
            {label}
          </label>
        )}
        <div
          className={classNames(
            'w-full flex bg-white border-gray-150 border border-l-2 border-l-primary',
            containerClassNames,
            { '!border !border-error': isError },
          )}
        >
          <div
            className={classNames(
              'flex-1 flex-start px-4 py-3',
              buttonContainerClassNames,
            )}
          >
            {!label && (
              <div className="mb-3 text-left text-xs text-gray-light uppercase">
                {t('Check-In')}
              </div>
            )}
            <div
              className={classNames(
                'flex justify-between items-center',
                buttonClassNames,
              )}
            >
              <span className="text-lg">{checkIn}</span>
              <CalendarInIcon className="fill-current text-primary" />
            </div>
          </div>
          <div
            className={classNames(
              'flex-1 px-4 py-3',
              buttonContainerClassNames,
            )}
          >
            {!label && (
              <div className="mb-3 text-left text-xs text-gray-light uppercase">
                {t('Check-Out')}
              </div>
            )}
            <div
              className={classNames(
                'flex justify-between items-center',
                buttonClassNames,
              )}
            >
              <span className="text-lg">{checkOut}</span>
              <CalendarOutIcon className="fill-current text-primary" />
            </div>
          </div>
        </div>
      </button>

      <Modal position="middle" open={open} onClose={closeCalendar}>
        <div className="relative bg-white shadow-lg text-left flex flex-col gap-2 w-[1004px] max-w-[92vw] max-h-[90vh] overflow-y-auto pt-14 px-6 pb-6">
          <button
            type="button"
            aria-label={t('Close this modal')}
            className="absolute top-4 right-6 z-10"
            onClick={closeCalendar}
          >
            <CloseIcon className="text-gray-light w-8 h-8" />
          </button>
          <div className="text-xs font-light uppercase px-4 py-2.5 bg-primary text-white">
            {headerTitle}
          </div>
          <div className="relative">
            {isLoading && (
              <div className="absolute top-0 left-0 w-full h-full bg-[#F6F6F6] bg-opacity-80 z-[500] flex items-center justify-center">
                <Loading
                  className="!text-[#F6F6F6] text-opacity-80"
                  baseClassName="!text-primary"
                />
              </div>
            )}
            <DateRangeSelect
              calendarRef={calendarRef}
              showDoubleView={true}
              currentActiveStartDate={currentActiveStartDate}
              selectedValue={selectedValue}
              today={today}
              disabledDates={disabledDates}
              ebisuDisabledDates={ebisuDisabledDates}
              onSelectValue={onSelectValue}
              handleCancel={handleCancel}
              setCurrentActiveStartDate={setCurrentActiveStartDate}
              onClose={() => setOpen(false)}
              onOpenMaxNightModal={onOpenMaxNightModal}
            />
          </div>
          <div className="flex items-center justify-center">
            <GuidelinePopup />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default PopupPicker
